import DateInput from "@/components/DateInput";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BancoCombobox from "@/components/BancoCombobox";
import CobroCombinadoEditor from "@/components/CobroCombinadoEditor";
import ProductoCombobox from "@/components/ProductoCombobox";
import MoneyInput from "@/components/MoneyInput";
import { Plus, Trash, ShieldCheck, Warning, Tag, X } from "@phosphor-icons/react";
import { fmtMoney, calcVencimiento } from "@/lib/api";
import {
  TIPOS_COMPROBANTE_MANUAL,
  esFiscal, requiereCuit, esFacturaA, esNotaCredito, esNotaDebito, defaultTipoAsociado,
  UNIDADES_NEGOCIO, TIPOS_COMP_ASOC, initForm,
} from "./constants";
import NumberInput from "@/components/ui/NumberInput";

export default function FacturaSheetForm({
  open, setOpen,
  isVenta, defaultUB, editingId, setEditingId,
  saving, save,
  form, setForm, setItem,
  afipRejection, setAfipRejection,
  contrapartes, cajas, mediosPago, listasPrecios, productos, insumos,
  promosAplicables, promosDescartadas, togglePromo,
  promosEfectivas, totalDescuentoPromos,
  subtotal, iva, total,
  setCobroOriginal,
}) {
  const addItem = () =>
    setForm({ ...form, items: [...form.items, { descripcion: "", tipo_producto: "", codigo: "", kilos: 0, cantidad: 1, precio_unitario: 0 }] });
  const removeItem = (i) =>
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  return (
      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setCobroOriginal(null); setForm(initForm({ unidad_negocio: defaultUB })); setAfipRejection(null); } }}>
        <SheetContent className="w-full sm:w-[780px] sm:max-w-[780px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {editingId ? "Editar comprobante" : (isVenta ? "Nueva Factura de Venta" : "Nueva Factura de Compra")}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={save} className="space-y-4 mt-6">
            {/* Banner AFIP cuando el tipo es fiscal (sólo en venta y creación) */}
            {isVenta && !editingId && esFiscal(form.tipo_comprobante) && (() => {
              const cp = contrapartes.find((c) => c.id === form.contraparte_id);
              const cuitOk = ((cp?.cuit || "").replace(/\D/g, "").length === 11);
              const necesitaCuit = requiereCuit(form.tipo_comprobante);
              const bloquea = necesitaCuit && !cuitOk;
              return (
                <div
                  data-testid="fac-banner-afip"
                  className={`border rounded-sm p-3 ${bloquea ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50/60"}`}
                >
                  <div className="flex items-start gap-2">
                    <ShieldCheck size={14} weight="bold" className={bloquea ? "text-red-700 mt-0.5" : "text-emerald-700 mt-0.5"} />
                    <div className="text-[12px] leading-snug">
                      {bloquea ? (
                        <>
                          <span className="font-semibold text-red-800">Falta CUIT del cliente.</span>{" "}
                          <span className="text-red-700">
                            {form.tipo_comprobante} requiere CUIT del receptor. Cargalo en la ficha del cliente o cambiá a Factura B.
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="font-semibold text-emerald-800">Se emitirá electrónicamente en AFIP.</span>{" "}
                          <span className="text-emerald-800">
                            Nº correlativo asignado por AFIP automáticamente y autorización al guardar.
                          </span>
                          {!necesitaCuit && !cuitOk && (
                            <div className="text-[11px] text-emerald-900 mt-1">
                              Sin CUIT: se emitirá como <b>Consumidor Final</b>.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <div>
                <Label className="text-[12px]">Tipo</Label>
                <Select value={form.tipo_comprobante} onValueChange={(v) => {
                  setForm({
                    ...form,
                    tipo_comprobante: v,
                    // Los remitos cárnicos sólo aplican a Factura A. Las NC/ND exigen
                    // Factura original asociada (AFIP CbteAsoc). Si el tipo nuevo no
                    // usa asociados, limpiamos. Si sí, preservamos lo que ya haya cargado.
                    comprobantes_asociados: (esFacturaA(v) || esNotaCredito(v) || esNotaDebito(v))
                      ? (form.comprobantes_asociados || [])
                      : [],
                  });
                  // Si el nuevo tipo es NO fiscal (Remito/Pedido/Ticket/Otro),
                  // limpiamos el banner de rechazo AFIP → el usuario puede guardar.
                  if (!esFiscal(v)) setAfipRejection(null);
                }}>
                  <SelectTrigger data-testid="fac-tipo-comp" className="rounded-sm mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_COMPROBANTE_MANUAL.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Nº Comprobante</Label>
                {isVenta && !editingId && esFiscal(form.tipo_comprobante) ? (
                  <Input
                    data-testid="fac-numero"
                    className="rounded-sm mt-1 font-mono bg-neutral-100 text-[#666]"
                    value="Auto AFIP"
                    disabled
                    readOnly
                    title="El número lo asigna AFIP al autorizar"
                  />
                ) : isVenta && !editingId ? (
                  <Input
                    data-testid="fac-numero"
                    className="rounded-sm mt-1 font-mono bg-neutral-100 text-[#666]"
                    value="Auto correlativo"
                    disabled
                    readOnly
                    title="El número se asigna automáticamente (0001-NNNNNNNN correlativo por tipo)"
                  />
                ) : (
                  <Input data-testid="fac-numero" className="rounded-sm mt-1 font-mono" value={form.numero}
                    onChange={(e) => setForm({ ...form, numero: e.target.value })} required placeholder="00001-00000001" />
                )}
              </div>
              <div>
                <Label className="text-[12px]">Fecha</Label>
                <DateInput data-testid="fac-fecha" className="rounded-sm mt-1 font-mono" value={form.fecha}
                  onChange={(e) => {
                    const nuevaFecha = e.target.value;
                    const cp = contrapartes.find((c) => c.id === form.contraparte_id);
                    const nuevoVto = form.vto_touched
                      ? form.fecha_vencimiento
                      : calcVencimiento(nuevaFecha, cp?.condicion_pago);
                    setForm({ ...form, fecha: nuevaFecha, fecha_vencimiento: nuevoVto });
                  }}
                  required />
              </div>
              <div>
                <Label className="text-[12px]">Vencimiento</Label>
                <DateInput
                  data-testid="fac-fecha-vto"
                  className="rounded-sm mt-1 font-mono"
                  value={form.fecha_vencimiento || form.fecha}
                  onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value, vto_touched: true })}
                />
              </div>
              <div>
                <Label className="text-[12px]">Romaneo</Label>
                <Input className="rounded-sm mt-1 font-mono" value={form.romaneo}
                  onChange={(e) => setForm({ ...form, romaneo: e.target.value })} placeholder="Nº" />
              </div>
            </div>

            {/* Comprobantes Asociados:
                 · Factura A → Remitos Cárnicos (obligación legal del rubro)
                 · Nota de Crédito / Nota de Débito → Factura original (AFIP exige CbteAsoc) */}
            {isVenta && (esFacturaA(form.tipo_comprobante) || esNotaCredito(form.tipo_comprobante) || esNotaDebito(form.tipo_comprobante)) && (
              <div className="border border-[#E5E5E5] rounded-sm bg-neutral-50/60">
                <div className="px-3 py-2 border-b bg-neutral-50 flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-widest font-mono text-[#6B1F24]">
                    {esNotaCredito(form.tipo_comprobante)
                      ? "Factura Asociada (obligatorio para NC · AFIP)"
                      : esNotaDebito(form.tipo_comprobante)
                        ? "Factura Asociada (obligatorio para ND · AFIP)"
                        : "Remitos Cárnicos Asociados"}
                  </div>
                  <Button
                    type="button"
                    data-testid="add-remito-carnico"
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-7 text-[11px]"
                    onClick={() => setForm({
                      ...form,
                      comprobantes_asociados: [
                        ...(form.comprobantes_asociados || []),
                        { tipo: defaultTipoAsociado(form.tipo_comprobante), pto_vta: "", nro: "", fecha: form.fecha },
                      ],
                    })}
                  >
                    <Plus size={12} weight="bold" className="mr-1" />
                    {esNotaCredito(form.tipo_comprobante) || esNotaDebito(form.tipo_comprobante)
                      ? "Asociar factura"
                      : "Agregar remito"}
                  </Button>
                </div>
                <div className="p-3 space-y-2">
                  {(form.comprobantes_asociados || []).length === 0 ? (
                    <div className="text-[11px] font-mono text-[#999] py-1">
                      {esNotaCredito(form.tipo_comprobante)
                        ? "Sin factura asociada. AFIP rechaza la NC sin CbteAsoc a la factura original — cargá tipo, punto de venta y número."
                        : esNotaDebito(form.tipo_comprobante)
                          ? "Sin factura asociada. AFIP rechaza la ND sin CbteAsoc a la factura original — cargá tipo, punto de venta y número."
                          : "Sin remitos asociados. Requerido para trasladar carne bovina/porcina que factures."}
                    </div>
                  ) : (
                    (form.comprobantes_asociados || []).map((cba, idx) => (
                      <div key={idx} data-testid={`comp-asoc-row-${idx}`} className="grid grid-cols-[1fr_100px_150px_140px_90px] gap-2 items-end">
                        <div>
                          {idx === 0 && <Label className="text-[10px] uppercase font-mono text-[#666] tracking-widest">Tipo</Label>}
                          <Select
                            value={String(cba.tipo)}
                            onValueChange={(v) => {
                              const next = [...form.comprobantes_asociados];
                              next[idx] = { ...next[idx], tipo: parseInt(v, 10) };
                              setForm({ ...form, comprobantes_asociados: next });
                            }}
                          >
                            <SelectTrigger className="rounded-sm text-[11px] h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TIPOS_COMP_ASOC.map((t) => (
                                <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] uppercase font-mono text-[#666] tracking-widest">P. Vta.</Label>}
                          <NumberInput
                            data-testid={`remito-carnico-pv-${idx}`} min="1" max="9999"
                            className="rounded-sm font-mono h-8 text-[12px]"
                            placeholder="0001"
                            value={cba.pto_vta}
                            onChange={(e) => {
                              const next = [...form.comprobantes_asociados];
                              next[idx] = { ...next[idx], pto_vta: e.target.value };
                              setForm({ ...form, comprobantes_asociados: next });
                            }}
                          />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] uppercase font-mono text-[#666] tracking-widest">Nº</Label>}
                          <Input type="number"
                            data-testid={`remito-carnico-nro-${idx}`} min="1"
                            className="rounded-sm font-mono h-8 text-[12px]"
                            placeholder="00000001"
                            value={cba.nro}
                            onChange={(e) => {
                              const next = [...form.comprobantes_asociados];
                              next[idx] = { ...next[idx], nro: e.target.value };
                              setForm({ ...form, comprobantes_asociados: next });
                            }}
                          />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] uppercase font-mono text-[#666] tracking-widest">Fecha</Label>}
                          <DateInput
                            className="rounded-sm font-mono h-8 text-[12px]"
                            value={cba.fecha || ""}
                            onChange={(e) => {
                              const next = [...form.comprobantes_asociados];
                              next[idx] = { ...next[idx], fecha: e.target.value };
                              setForm({ ...form, comprobantes_asociados: next });
                            }}
                          />
                        </div>
                        <div>
                          {idx === 0 && <Label className="text-[10px] uppercase font-mono text-[#666] tracking-widest invisible">Acción</Label>}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid={`remito-carnico-del-${idx}`}
                            onClick={() => {
                              if (!window.confirm("¿Eliminar este remito cárnico asociado?")) return;
                              const next = form.comprobantes_asociados.filter((_, i) => i !== idx);
                              setForm({ ...form, comprobantes_asociados: next });
                            }}
                            className="h-8 w-full rounded-sm text-[11px] font-mono uppercase tracking-widest border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 hover:border-red-500"
                            title="Quitar remito"
                          >
                            <Trash size={12} weight="bold" className="mr-1" />
                            Quitar
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                  <div className="text-[10px] font-mono text-[#999] pt-1">
                    Cargá los remitos electrónicos cárnicos ya emitidos en AFIP. Podés eliminarlos con "Quitar" antes de confirmar el comprobante. Se enviarán como CbtesAsoc al pedir CAE.
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="col-span-2">
                <Label className="text-[12px]">{isVenta ? "Cliente" : "Proveedor"}</Label>
                <Select
                  value={form.contraparte_id}
                  onValueChange={(v) => {
                    // Al seleccionar cliente, adoptar su lista de precios predeterminada si tiene
                    const cp = contrapartes.find((c) => c.id === v);
                    const nuevoVto = form.vto_touched
                      ? form.fecha_vencimiento
                      : calcVencimiento(form.fecha, cp?.condicion_pago);
                    setForm({
                      ...form,
                      contraparte_id: v,
                      lista_precios_id: isVenta
                        ? (cp?.lista_precios_id || form.lista_precios_id || (listasPrecios[0]?.id || ""))
                        : form.lista_precios_id,
                      fecha_vencimiento: nuevoVto,
                    });
                  }}
                >
                  <SelectTrigger data-testid="fac-contraparte" className="rounded-sm mt-1">
                    <SelectValue placeholder="Seleccionar…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {contrapartes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isVenta && listasPrecios.length > 0 && (
                  <div className="mt-2">
                    <Label className="text-[12px]">Lista de precios</Label>
                    <Select
                      value={form.lista_precios_id || (listasPrecios[0]?.id || "")}
                      onValueChange={(v) => setForm({ ...form, lista_precios_id: v })}
                    >
                      <SelectTrigger data-testid="fac-lista-precios" className="rounded-sm mt-1">
                        <SelectValue placeholder="Seleccionar lista…" />
                      </SelectTrigger>
                      <SelectContent>
                        {listasPrecios.map((l) => {
                          const cp = contrapartes.find((c) => c.id === form.contraparte_id);
                          const isDefault = cp?.lista_precios_id === l.id;
                          return (
                            <SelectItem key={l.id} value={l.id}>
                              <div className="flex items-center gap-2">
                                <span>{l.nombre}</span>
                                {isDefault && (
                                  <span className="text-[9px] font-mono uppercase text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded-sm">
                                    predet.
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <div className="text-[10px] text-[#666] font-mono mt-1">
                      Al seleccionar un producto se usará el precio de esta lista (fallback: precio actual).
                    </div>
                  </div>
                )}
                {isVenta && esFiscal(form.tipo_comprobante) && (() => {
                  const cp = contrapartes.find((c) => c.id === form.contraparte_id);
                  const condCliente = cp?.condicion_iva || "sin condición cargada";
                  return (
                    <div className="mt-2">
                      <Label className="text-[12px] flex items-center gap-1">
                        Condición IVA receptor
                        <span className="text-[10px] font-mono text-[#888] normal-case">(override)</span>
                      </Label>
                      <Select
                        value={form.condicion_iva_receptor_override || "__cliente__"}
                        onValueChange={(v) => setForm({ ...form, condicion_iva_receptor_override: v === "__cliente__" ? "" : v })}
                      >
                        <SelectTrigger data-testid="fac-condicion-iva-override" className="rounded-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__cliente__">Usar la del cliente ({condCliente})</SelectItem>
                          <SelectItem value="Responsable Inscripto">Responsable Inscripto</SelectItem>
                          <SelectItem value="Monotributista">Monotributista</SelectItem>
                          <SelectItem value="Consumidor Final">Consumidor Final</SelectItem>
                          <SelectItem value="Sujeto Exento">Sujeto Exento</SelectItem>
                          <SelectItem value="Monotributo Social">Monotributo Social</SelectItem>
                          <SelectItem value="No Categorizado">Sujeto No Categorizado</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-[10px] text-[#666] font-mono mt-1">
                        Sólo afecta a esta factura. La ficha del cliente queda intacta.
                      </div>
                    </div>
                  );
                })()}
              </div>
              {isVenta && (
                <div className="col-span-2">
                  <Label className="text-[12px]">
                    Unidad de negocio <span className="text-red-600">*</span>
                  </Label>
                  <Select value={form.unidad_negocio} onValueChange={(v) => setForm({ ...form, unidad_negocio: v })}>
                    <SelectTrigger data-testid="fac-unidad" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNIDADES_NEGOCIO.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!isVenta && (
                <>
                  <div>
                    <Label className="text-[12px]">Moneda</Label>
                    <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ARS">ARS</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[12px]">Cotización</Label>
                    <NumberInput className="rounded-sm mt-1 font-mono" step="0.01" value={form.cotizacion}
                      onChange={(e) => setForm({ ...form, cotizacion: e.target.value })} />
                  </div>
                </>
              )}
            </div>

            <div className="border border-[#E5E5E5] rounded-sm">
              <div className="px-3 py-2 bg-neutral-50 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest font-mono text-[#666]">Ítems</span>
                <Button type="button" onClick={addItem} size="sm" variant="outline" className="rounded-sm h-7 text-[11px]">
                  <Plus size={12} className="mr-1" /> Agregar
                </Button>
              </div>
              <div className="divide-y">
                {form.items.map((it, i) => {
                  // Detección: para compras con proveedor tipo servicios_insumos, el picker
                  // cambia a InsumoCombobox + concepto libre. Sino, ProductoCombobox estándar.
                  const cpSel = contrapartes.find((c) => c.id === form.contraparte_id);
                  const tipoProv = cpSel?.tipo_proveedor || "productos";
                  const isServiciosMode = !isVenta && tipoProv === "servicios_insumos";
                  const isConceptoLibre = !!it._concepto_libre;
                  return (
                    <div key={i} className="p-3 grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-6">
                        <Label className="text-[10px]">
                          {isServiciosMode
                            ? (isConceptoLibre ? "Concepto libre" : "Insumo / Materia prima")
                            : "Producto"}
                        </Label>
                        {isServiciosMode ? (
                          <div className="space-y-1">
                            {isConceptoLibre ? (
                              <>
                                <Input
                                  data-testid={`vitem-descripcion-${i}`}
                                  className="rounded-sm h-8 text-[12px]"
                                  placeholder="Ej: Luz Edenor mayo, Diseño folletos…"
                                  value={it.descripcion}
                                  onChange={(e) => setItem(i, "descripcion", e.target.value)}
                                />
                                <Select
                                  value={it.categoria_gasto || "otros"}
                                  onValueChange={(v) => setItem(i, "categoria_gasto", v)}
                                >
                                  <SelectTrigger className="rounded-sm h-7 text-[11px]" data-testid={`vitem-categoria-${i}`}>
                                    <SelectValue placeholder="Categoría" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="servicios">Servicios (luz, gas, agua, internet)</SelectItem>
                                    <SelectItem value="marketing">Marketing / Publicidad</SelectItem>
                                    <SelectItem value="impuestos">Impuestos / Tasas</SelectItem>
                                    <SelectItem value="otros">Otros</SelectItem>
                                  </SelectContent>
                                </Select>
                                <button type="button"
                                  className="text-[10px] font-mono text-neutral-500 hover:text-neutral-800 underline"
                                  onClick={() => {
                                    const items = [...form.items];
                                    items[i] = { ...items[i], _concepto_libre: false, descripcion: "", categoria_gasto: null, insumo_id: null };
                                    setForm({ ...form, items });
                                  }}
                                >
                                  ← elegir un insumo del catálogo
                                </button>
                              </>
                            ) : (
                              <>
                                <Select
                                  value={it.insumo_id || ""}
                                  onValueChange={(v) => {
                                    const ins = insumos.find((x) => x.id === v);
                                    if (!ins) return;
                                    const items = [...form.items];
                                    items[i] = {
                                      ...items[i],
                                      insumo_id: ins.id,
                                      descripcion: ins.nombre,
                                      codigo: ins.codigo || null,
                                      precio_unitario: ins.ultimo_costo || ins.precio_actual || items[i].precio_unitario || 0,
                                      producto_id: null,
                                      categoria_gasto: null,
                                    };
                                    setForm({ ...form, items });
                                  }}
                                >
                                  <SelectTrigger className="rounded-sm h-8 text-[12px]" data-testid={`vitem-insumo-${i}`}>
                                    <SelectValue placeholder="Elegí un insumo…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {insumos.map((ins) => (
                                      <SelectItem key={ins.id} value={ins.id}>
                                        {ins.nombre}
                                        {ins.codigo && <span className="text-[10px] text-neutral-400 ml-1">({ins.codigo})</span>}
                                      </SelectItem>
                                    ))}
                                    {insumos.length === 0 && (
                                      <div className="p-2 text-[11px] text-neutral-400">Todavía no hay insumos cargados.</div>
                                    )}
                                  </SelectContent>
                                </Select>
                                <button type="button"
                                  className="text-[10px] font-mono text-blue-700 hover:text-blue-900 underline"
                                  data-testid={`vitem-concepto-libre-${i}`}
                                  onClick={() => {
                                    const items = [...form.items];
                                    items[i] = { ...items[i], _concepto_libre: true, insumo_id: null, codigo: null, descripcion: "" };
                                    setForm({ ...form, items });
                                  }}
                                >
                                  + Cargar concepto libre (sin insumo del catálogo)
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <ProductoCombobox
                            kind="productos"
                            testid={`vitem-combo-${i}`}
                            value={{ codigo: it.codigo, nombre: it.descripcion }}
                            onSelect={(p) => {
                              const items = [...form.items];
                              const prod = productos.find((x) => x.codigo === p.codigo) || productos.find((x) => x.nombre === p.nombre);
                              const listaId = form.lista_precios_id || listasPrecios[0]?.id || "";
                              // BUG FIX: nunca caer silenciosamente a otra lista de precios. Si el producto no tiene
                              // precio en la lista del cliente → precio_actual + toast de aviso (evita traer "Minorista"
                              // en clientes mayoristas).
                              const priceFromList = listaId && prod?.precios ? prod.precios[listaId] : null;
                              const salePrice = (priceFromList && priceFromList > 0)
                                ? priceFromList
                                : (prod?.precio_actual || p.precio_actual || 0);
                              if (isVenta && listaId && !(priceFromList && priceFromList > 0)) {
                                const lista = listasPrecios.find((l) => l.id === listaId);
                                toast.warning(
                                  `"${p.nombre}" no tiene precio en "${lista?.nombre || "la lista seleccionada"}". Se usó precio actual. Verificá manualmente.`,
                                  { duration: 6000 }
                                );
                              }
                              items[i] = {
                                ...items[i],
                                codigo: p.codigo,
                                descripcion: p.nombre,
                                producto_id: prod?.id || p.id || null,
                                insumo_id: null,
                                categoria_gasto: null,
                                // En compras: precargar el último costo (0 si no hay).
                                // En ventas: precio de lista del cliente (sin fallback a otras listas).
                                precio_unitario: !isVenta ? (prod?.ultimo_costo || 0) : salePrice,
                              };
                              setForm({ ...form, items });
                            }}
                          />
                        )}
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px]">Kilos</Label>
                        <MoneyInput className="rounded-sm h-8 font-mono text-[12px] text-right" step="0.001"
                          data-testid={`vitem-kilos-${i}`}
                          value={it.kilos} onChange={(e) => setItem(i, "kilos", e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px]">Precio/u c/IVA</Label>
                        <MoneyInput className="rounded-sm h-8 font-mono text-[12px] text-right" step="0.01"
                          data-testid={`vitem-precio-${i}`}
                          value={it.precio_unitario} onChange={(e) => setItem(i, "precio_unitario", e.target.value)} />
                      </div>
                      <div className="col-span-1 text-right font-mono text-[12px] pt-4">
                        {((parseFloat(it.kilos) || parseFloat(it.cantidad) || 0) * (parseFloat(it.precio_unitario) || 0)).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <button type="button" onClick={() => removeItem(i)}
                        className="col-span-1 text-red-600 hover:text-red-800 pt-4">
                        <Trash size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Flags de compra: actualizar costo (la compra NO mueve stock) */}
            {!isVenta && (
              <div className="border border-blue-200 bg-blue-50/40 rounded-sm p-3 flex flex-wrap items-center gap-4" data-testid="compra-flags">
                <div className="text-[11px] uppercase font-mono text-blue-800 tracking-wider">
                  Al confirmar la compra:
                </div>
                <label className="flex items-center gap-2 text-[12px] cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-[#6B1F24]"
                    checked={!!form.actualiza_costo}
                    onChange={(e) => setForm({ ...form, actualiza_costo: e.target.checked })}
                    data-testid="compra-actualiza-costo"
                  />
                  Actualizar costo de última compra
                </label>
                <div className="text-[10px] text-neutral-500 italic ml-2">
                  El stock lo mueve la Recepción / Ingreso de Mercadería, no la factura.
                </div>
              </div>
            )}

            {/* Código de descuento (cupón) — se envía a /promociones/aplicables como `codigo`. */}
            {isVenta && (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label htmlFor="factura-codigo-promo" className="text-[11px] uppercase tracking-widest font-mono text-neutral-500">
                    Código de descuento (cupón)
                  </Label>
                  <Input
                    id="factura-codigo-promo"
                    data-testid="factura-codigo-promo"
                    value={form.codigo_promo || ""}
                    onChange={(e) => setForm({ ...form, codigo_promo: e.target.value.toUpperCase() })}
                    placeholder="Ej: MARZO10"
                    className="rounded-sm font-mono uppercase mt-1"
                  />
                  <div className="text-[10px] text-neutral-500 mt-1 font-mono">
                    Ingresá un código para desbloquear promos tipo cupón. Las promos sin código aplican siempre.
                  </div>
                </div>
              </div>
            )}

            {/* Promociones aplicables (auto). Se muestra también al editar. */}
            {isVenta && promosAplicables.length > 0 && (
              <div className="border border-emerald-300 rounded-sm bg-emerald-50/40" data-testid="promos-panel">
                <div className="px-3 py-2 bg-emerald-100 border-b border-emerald-300 flex items-center gap-2">
                  <Tag size={14} weight="fill" className="text-emerald-700" />
                  <span className="text-[11px] uppercase tracking-widest font-mono text-emerald-800">
                    Promociones aplicables · {promosEfectivas.length} de {promosAplicables.length}
                  </span>
                  <span className="ml-auto text-[11px] font-mono text-emerald-800">
                    Descuento total: {fmtMoney(totalDescuentoPromos, form.moneda)}
                  </span>
                </div>
                <div className="divide-y">
                  {promosAplicables.map((p) => {
                    const descartada = promosDescartadas.has(p.promocion_id);
                    const lineas = p.lineas_afectadas || [];
                    return (
                      <div
                        key={p.promocion_id}
                        className={`p-2 flex flex-col gap-1 text-[12px] ${descartada ? "opacity-50" : ""}`}
                        data-testid={`promo-aplicable-${p.promocion_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className={`font-medium ${descartada ? "line-through" : "text-emerald-800"}`}>
                              {p.promocion_nombre}
                            </div>
                            <div className="text-[10px] text-neutral-500 font-mono">
                              {p.descuento_tipo === "porcentaje" && `-${p.descuento_valor}%`}
                              {p.descuento_tipo === "monto_por_unidad" && `-$${p.descuento_valor} / u`}
                              {p.descuento_tipo === "precio_fijo" && `$${p.descuento_valor} c/u fijo`}
                              {p.es_por_medio_pago
                                ? ` · sobre subtotal neto ${fmtMoney(p.subtotal_neto_base || 0, form.moneda)}`
                                : (p.kilos_a_consumir > 0 ? ` · ${p.kilos_a_consumir.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg` : "")}
                            </div>
                          </div>
                          <div className="text-right font-mono font-semibold text-emerald-800">
                            - {fmtMoney(p.importe_descuento_total, form.moneda)}
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePromo(p.promocion_id)}
                            className={`text-[11px] px-2 py-1 rounded-sm border ${descartada ? "bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50" : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"}`}
                            data-testid={`toggle-promo-${p.promocion_id}`}
                            title={descartada ? "Restaurar promoción" : "Quitar de la venta"}
                          >
                            {descartada ? "Restaurar" : "Quitar"}
                          </button>
                        </div>
                        {lineas.length > 0 && !descartada && (
                          <div className="ml-2 pl-2 border-l-2 border-emerald-200 space-y-0.5">
                            {lineas.map((l, li) => (
                              <div key={li} className="flex items-center gap-2 text-[10px] text-neutral-600 font-mono">
                                <span className="text-emerald-700">↳</span>
                                <span className="flex-1 truncate">{l.descripcion || `Ítem #${l.idx + 1}`}</span>
                                <span>{l.kilos > 0 ? `${l.kilos.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg` : `${l.cantidad} u`}</span>
                                <span className="text-emerald-700 font-semibold">- {fmtMoney(l.importe_descuento, form.moneda)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[12px]">IVA (%)</Label>
                <NumberInput className="rounded-sm mt-1 font-mono" step="0.01" value={form.iva_pct}
                  onChange={(e) => setForm({ ...form, iva_pct: e.target.value })} />
              </div>
              <div className="col-span-2 flex justify-end gap-8 pt-6 font-mono text-[13px]">
                <div>
                  <div className="text-[10px] uppercase text-[#666]">Sin IVA</div>
                  <div>{fmtMoney(subtotal, form.moneda)}</div>
                </div>
                {totalDescuentoPromos > 0 && (
                  <div>
                    <div className="text-[10px] uppercase text-emerald-700">Descuento</div>
                    <div className="text-emerald-700">- {fmtMoney(totalDescuentoPromos, form.moneda)}</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase text-[#666]">IVA</div>
                  <div>{fmtMoney(iva, form.moneda)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#666]">Total</div>
                  <div className="font-semibold text-[15px]">{fmtMoney(total, form.moneda)}</div>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[12px]">Observaciones</Label>
              <Input className="rounded-sm mt-1" value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
            </div>

            {isVenta && (
              <div className="border border-[#E5E5E5] rounded-sm bg-[#F5F1E9]/40">
                <div className="px-3 py-2 bg-[#F5F1E9] border-b border-[#E5E5E5] flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-widest font-mono text-[#6B1F24]">
                    Medio de pago
                  </span>
                  <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider cursor-pointer select-none" data-testid="factura-cobro-combinado-toggle">
                    <input
                      type="checkbox"
                      checked={Array.isArray(form.pagos_combinados)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setForm({
                            ...form,
                            medio_pago: "cta_cte",
                            pagos_combinados: [{ medio_pago: "", monto: 0, cheque_info: null }],
                          });
                        } else {
                          setForm({ ...form, pagos_combinados: null });
                        }
                      }}
                    />
                    Cobro combinado
                  </label>
                </div>
                <div className="p-3">
                  {Array.isArray(form.pagos_combinados) ? (
                    <CobroCombinadoEditor
                      lineas={form.pagos_combinados}
                      total={total}
                      moneda={form.moneda}
                      cajas={cajas}
                      mediosPago={mediosPago}
                      fecha={form.fecha}
                      onChange={(nuevas) => setForm({ ...form, pagos_combinados: nuevas })}
                    />
                  ) : (
                  <>
                  <Select value={form.medio_pago} onValueChange={(v) => setForm({ ...form, medio_pago: v })}>
                    <SelectTrigger data-testid="factura-medio-pago" className="rounded-sm bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cta_cte">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-mono uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-sm">
                            CTA CTE
                          </span>
                          <span>Cuenta corriente (pendiente de cobro)</span>
                        </div>
                      </SelectItem>
                      {mediosPago.length > 0
                        ? mediosPago.filter((m) => m.caja_id).map((m) => (
                            <SelectItem key={m.id} value={m.caja_id}>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-mono uppercase text-[#666] bg-neutral-100 px-1.5 py-0.5 rounded-sm">
                                  {m.tipo}
                                </span>
                                <span>{m.nombre}</span>
                                {m.caja_nombre && m.caja_nombre !== m.nombre && (
                                  <span className="text-[10px] font-mono text-neutral-400">→ {m.caja_nombre}</span>
                                )}
                              </div>
                            </SelectItem>
                          ))
                        : cajas.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-mono uppercase text-[#666] bg-neutral-100 px-1.5 py-0.5 rounded-sm">
                                  {c.tipo}
                                </span>
                                <span>{c.nombre}</span>
                              </div>
                            </SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-[#666] font-mono mt-2">
                    {form.medio_pago === "cta_cte"
                      ? "La factura queda pendiente y suma al saldo del cliente."
                      : `Se generará un cobro automático por el total en ${
                          (mediosPago.find((m) => m.caja_id === form.medio_pago)?.caja_nombre)
                          || (cajas.find((c) => c.id === form.medio_pago)?.nombre)
                          || "la caja"
                        }.`}
                  </div>
                  {/* Cobro en efectivo en la ruta: solo aplica a ventas en Cta. Cte. */}
                  {isVenta && form.medio_pago === "cta_cte" && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cobrar_efectivo_en_ruta: !form.cobrar_efectivo_en_ruta })}
                      data-testid="factura-cobrar-efectivo-ruta"
                      className={`mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-sm border-2 text-left transition ${
                        form.cobrar_efectivo_en_ruta
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-dashed border-neutral-300 bg-white hover:bg-neutral-50 hover:border-neutral-400"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center shrink-0 ${
                        form.cobrar_efectivo_en_ruta ? "bg-emerald-500 border-emerald-500" : "border-neutral-400 bg-white"
                      }`}>
                        {form.cobrar_efectivo_en_ruta && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                      </span>
                      <span className="flex-1 min-w-0">
                        <div className={`text-[12px] font-medium ${form.cobrar_efectivo_en_ruta ? "text-emerald-800" : "text-neutral-700"}`}>
                          ¿Se cobra en efectivo en la ruta?
                        </div>
                        <div className="text-[10px] text-neutral-500 font-mono">
                          {form.cobrar_efectivo_en_ruta
                            ? "El chofer cobra el total al entregar. Aparecerá en la Hoja de Reparto."
                            : "Marcá esta opción si el cliente le paga al chofer al momento de la entrega."}
                        </div>
                      </span>
                    </button>
                  )}
                  {/* Mini-form Cheque cuando el medio apunta a caja tipo "cheques" */}
                  {(() => {
                    const cajaSel = cajas.find((c) => c.id === form.medio_pago);
                    if (cajaSel?.tipo !== "cheques") return null;
                    const ci = form.cheque_info || {};
                    const setCI = (patch) => setForm({ ...form, cheque_info: { ...ci, ...patch } });
                    return (
                      <div className="mt-3 border border-blue-200 bg-blue-50/40 rounded-sm p-3 space-y-2" data-testid="cheque-form">
                        <div className="text-[11px] uppercase font-mono text-blue-800 tracking-wider">
                          Datos del cheque — se registrarán en el módulo Cheques Recibidos
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[11px]">Número</Label>
                            <Input
                              data-testid="cheque-numero"
                              className="rounded-sm mt-0.5 font-mono h-8"
                              value={ci.numero || ""}
                              onChange={(e) => setCI({ numero: e.target.value })}
                              placeholder="Ej: 12345678"
                            />
                          </div>
                          <div>
                            <Label className="text-[11px]">Banco (Nómina BCRA)</Label>
                            <div className="mt-0.5">
                              <BancoCombobox
                                testId="cheque-banco"
                                value={ci.banco_code ? { code: ci.banco_code, name: ci.banco_nombre } : null}
                                onChange={(b) => setCI({ banco_code: b.code, banco_nombre: b.name })}
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-[11px]">Fecha emisión</Label>
                            <DateInput
                              data-testid="cheque-fecha-emision"
                              className="rounded-sm mt-0.5 font-mono h-8"
                              value={ci.fecha_emision || form.fecha}
                              onChange={(e) => setCI({ fecha_emision: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label className="text-[11px]">Fecha cobro</Label>
                            <DateInput
                              data-testid="cheque-fecha-cobro"
                              className="rounded-sm mt-0.5 font-mono h-8"
                              value={ci.fecha_cobro || ""}
                              onChange={(e) => setCI({ fecha_cobro: e.target.value })}
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[11px]">Firmante (nombre del librador)</Label>
                            <Input
                              data-testid="cheque-firmante"
                              className="rounded-sm mt-0.5 h-8"
                              value={ci.firmante || ""}
                              onChange={(e) => setCI({ firmante: e.target.value })}
                              placeholder="Ej: Distribuidora XX SA"
                            />
                          </div>
                          <div>
                            <Label className="text-[11px]">CUIT emisor</Label>
                            <Input
                              data-testid="cheque-cuit"
                              className="rounded-sm mt-0.5 font-mono h-8"
                              value={ci.cuit_emisor || ""}
                              onChange={(e) => setCI({ cuit_emisor: e.target.value })}
                              placeholder="20-12345678-9"
                            />
                          </div>
                          <div>
                            <Label className="text-[11px]">Recibido por</Label>
                            <Input
                              data-testid="cheque-recibido-por"
                              className="rounded-sm mt-0.5 h-8"
                              value={ci.recibido_por || ""}
                              onChange={(e) => setCI({ recibido_por: e.target.value })}
                              placeholder="Nombre de quien recibe"
                            />
                          </div>
                        </div>
                        <div className="text-[10px] font-mono text-blue-800/70">
                          Al guardar, el cheque queda en <b>Tesorería → Cheques Recibidos</b> como <i>aceptado</i>.
                        </div>
                      </div>
                    );
                  })()}
                  </>
                  )}
                </div>
              </div>
            )}

            {/* Banner de rechazo AFIP — el usuario debe cambiar el tipo o corregir para reintentar */}
            {afipRejection && esFiscal(form.tipo_comprobante) && (
              <div className="border-2 border-red-400 bg-red-50 rounded-sm p-3 space-y-2" data-testid="afip-rejection-banner">
                <div className="flex items-start gap-2">
                  <Warning size={18} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-red-800 uppercase tracking-wider mb-1">
                      AFIP rechazó la factura
                    </div>
                    <div className="text-[12px] text-red-900 font-mono break-words">
                      {afipRejection}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAfipRejection(null)}
                    className="text-red-500 hover:text-red-700 text-lg leading-none"
                    aria-label="Cerrar"
                  >×</button>
                </div>
                <div className="border-t border-red-200 pt-2 space-y-1">
                  <div className="text-[11px] text-red-800">
                    Corregí los datos y reintentá <b>Registrar venta</b>, o cambiá el tipo a uno sin CAE para guardarlo internamente:
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {["REMITO X", "PEDIDO", "TICKET", "OTRO"].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          setForm((f) => ({ ...f, tipo_comprobante: t, comprobantes_asociados: [] }));
                          setAfipRejection(null);
                        }}
                        className="text-[11px] font-mono uppercase px-2 py-1 rounded-sm border border-red-300 bg-white hover:bg-red-100 text-red-900"
                        data-testid={`afip-rej-cambiar-${t}`}
                      >
                        Cambiar a {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <Button
              data-testid="save-factura-btn"
              type="submit"
              disabled={saving || (!!afipRejection && esFiscal(form.tipo_comprobante))}
              className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {saving
                ? (editingId ? (esFiscal(form.tipo_comprobante) ? "Guardando y emitiendo en AFIP…" : "Guardando…") : (esFiscal(form.tipo_comprobante) ? "Emitiendo en AFIP…" : "Registrando…"))
                : (afipRejection && esFiscal(form.tipo_comprobante)
                    ? "Elegí un tipo sin CAE o corregí los datos"
                    : (editingId ? "Guardar cambios" : (isVenta ? "Registrar venta" : "Registrar compra")))}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
  );
}
